/**
 * Sentry Configuration
 *
 * This file MUST be imported before any other code in main.tsx
 * to ensure Sentry captures all errors from application start.
 *
 * Features:
 * - Performance monitoring with browser tracing and profiling
 * - Session replay with comprehensive privacy controls
 * - HTTP client error tracking
 * - User feedback on error (via ErrorBoundary showDialog)
 * - Sensitive data filtering (PII, Mexican identifiers, tokens)
 */
import * as Sentry from "@sentry/react";
import {
  containsSensitiveText,
  scrubBreadcrumbs,
} from "./utils/sentryBreadcrumbScrubber";

// Environment variables - defined inline for consistency with app-express
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
const APP_VERSION = import.meta.env.VITE_APP_VERSION || "0.0.0";
const IS_PRODUCTION = import.meta.env.PROD;

Sentry.init({
  dsn: SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  release: `mb-multiexpediente-app@${APP_VERSION}`,
  enabled: IS_PRODUCTION && !!SENTRY_DSN,

  // Enable Sentry Logs for cross-session analysis and backend correlation
  _experiments: {
    enableLogs: true,
  },

  integrations: [
    // Browser tracing is handled by Sentry.wrapCreateBrowserRouterV6 in App.tsx
    // Browser profiling for performance analysis
    Sentry.browserProfilingIntegration(),
    // HTTP client integration for capturing failed HTTP requests
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [[500, 599]],
    }),
    // Session Replay with comprehensive privacy controls
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
      // Mask sensitive content - use data-sentry-mask attribute in components
      mask: ["[data-sentry-mask]"],
      // Block elements from being recorded entirely
      block: [
        ".sigCanvas", // Signature canvas
        "[data-sentry-block]",
      ],
      // Ignore input values (never captured)
      ignore: [
        'input[type="password"]',
        'input[type="email"]',
        'input[type="tel"]',
        'input[name*="card"]',
        'input[name*="cvv"]',
        'input[name*="curp"]',
        'input[name*="rfc"]',
        'input[name*="clabe"]',
        'input[name*="nss"]',
        'input[name*="ine"]',
        'input[name*="account"]',
        'input[name*="password"]',
        'input[name*="token"]',
        'textarea[name*="notes"]',
        "[data-sentry-ignore]",
      ],
      networkRequestHeaders: ["Content-Type", "Accept"],
      networkResponseHeaders: ["Content-Type", "X-Request-Id"],
    }),
  ],

  // Performance Monitoring - sample 10% of transactions in production
  tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0,

  // Profiling - sample rate for continuous profiling (v10.27.0+)
  profileSessionSampleRate: IS_PRODUCTION ? 0.1 : 1.0,

  // Session Replay - only capture sessions with errors (no regular session sampling)
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  // Track API calls to your backend (distributed tracing)
  tracePropagationTargets: [
    /^\//, // Relative URLs (same origin)
    /^https?:\/\/.*\.multiburo\.com\.mx/, // *.multiburo.com.mx
  ],

  // Increase depth for Redux state serialization
  normalizeDepth: 5,

  // Filter sensitive data before sending to Sentry
  beforeSend(event, hint) {
    const error = hint?.originalException as Error & {
      response?: { status?: number };
      status?: number;
    };

    // Ignore 403 Forbidden errors (auth/permission issues handled by app)
    if (
      error?.response?.status === 403 ||
      error?.status === 403 ||
      event.exception?.values?.some((e) => e.value?.includes("403"))
    ) {
      return null;
    }

    // Remove sensitive data from breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = scrubBreadcrumbs(event.breadcrumbs);
    }

    // Remove sensitive headers from request data
    if (event.request?.headers) {
      const sensitiveHeaders = [
        "Authorization",
        "authorization",
        "Cookie",
        "cookie",
        "X-Auth-Token",
        "x-auth-token",
      ];
      sensitiveHeaders.forEach((h) => {
        delete event.request?.headers?.[h];
      });
    }

    // Remove sensitive cookies
    if (event.request?.cookies) {
      const filteredCookies = { ...event.request.cookies };
      delete filteredCookies.session;
      delete filteredCookies.token;
      delete filteredCookies.userToken;
      event.request.cookies = filteredCookies;
    }

    // Filter sensitive request body data (handle both strings and objects)
    if (event.request?.data) {
      const dataStr =
        typeof event.request.data === "string"
          ? event.request.data
          : JSON.stringify(event.request.data);

      const bodyContainsSensitive = containsSensitiveText(dataStr);

      if (bodyContainsSensitive) {
        event.request.data = "[Filtered]";
      }
    }

    return event;
  },

  // Ignore common non-actionable errors
  ignoreErrors: [
    // Network errors
    "Network Error",
    "Failed to fetch",
    "Load failed",
    "NetworkError when attempting to fetch resource",
    "The request timed out",
    "timeout of 0ms exceeded",
    "The operation was aborted",
    // HTTP 403 Forbidden (usually auth/permission issues handled by app)
    /Request failed with status code 403/,
    /status code 403/i,
    /403 Forbidden/i,
    // Abort errors
    "AbortError",
    "cancelled",
    "Aborted",
    // Browser extensions and third-party scripts
    "top.GLOBALS",
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-extension:\/\//,
    // Script errors from third-parties
    /^Script error\.?$/,
    /^Javascript error: Script error\.? on line 0$/,
    // ResizeObserver errors (browser quirk)
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    // PDF.js errors
    "Setting up fake worker",
    "Worker was destroyed",
    "Cannot read properties of null (reading 'promise')",
    // OpenCV.js errors - self-hosted opencv.js
    "OpenCV.js is not ready",
    "cv is not defined",
    // Common Ant Design errors
    "Cannot read properties of null (reading 'getBoundingClientRect')",
    "Cannot read properties of undefined (reading 'contains')",
  ],

  // Block errors from specific URLs
  denyUrls: [
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    // Third-party analytics and tracking
    /google-analytics\.com/i,
    /googletagmanager\.com/i,
    /hotjar\.com/i,
    /intercom\.io/i,
    /cdn\.segment\.com/i,
    /facebook\.net/i,
  ],
});

export default Sentry;
